import { isDefined } from "@clipboard-health/util-ts";
import { useFavoriteWorkplacesFlag } from "@src/appV2/FeatureFlags/useFavoriteWorkplacesFlag";
import { useDebounce } from "@src/appV2/lib/utils/useDebounce";
import { useWorkerGeoLocation } from "@src/appV2/Worker/useWorkerGeoLocation";
import { useFavoriteWorkplaces } from "@src/appV2/Workplaces/api/useFavoriteWorkplaces";
import { useQualifiedWorkplaces } from "@src/appV2/Workplaces/api/useQualifiedWorkplaces";
import { type InfiniteData } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { useOtherWorkplaces } from "../WorkplacesList/useOtherWorkplaces";

export interface WorkplaceItem {
  workplaceId: string;
  workplaceName: string;
  workplaceType?: string;
  workplaceStatus?: string;
  openShiftCount: number | undefined;
  distanceFromWorker: number | undefined;
  favoriteId?: string;
}

interface PlaceholderItem {
  id: string;
  type: "placeholder";
}

export interface WorkplaceGroupItem extends WorkplaceItem {
  type: "workplace";
}

export type GroupedItem = (PlaceholderItem | WorkplaceGroupItem) & {
  key: string;
  groupId: "favorite" | "active" | "other";
};

export type ItemGroups = Record<
  GroupedItem["groupId"],
  { items: GroupedItem[]; count?: number; title: string }
>;

function createWorkplaceGroupedItem(params: {
  workplaceId: string;
  workplaceName: string | undefined;
  groupId: GroupedItem["groupId"];
  openShiftCount?: number;
  distanceFromWorker?: number;
  favoriteId?: string;
}): GroupedItem {
  const { workplaceId, workplaceName, groupId, openShiftCount, distanceFromWorker, favoriteId } =
    params;

  return {
    key: workplaceId,
    type: "workplace",
    groupId,
    workplaceId,
    workplaceName: workplaceName ?? "",
    openShiftCount,
    distanceFromWorker,
    favoriteId,
  };
}

function createDeduplicatedWorkplaceItems(params: {
  workplaces: InfiniteData<{
    data: Array<{
      id: string;
      attributes: {
        name?: string | undefined;
        distanceFromWorker?: number | undefined;
        openShiftCount?: number | undefined;
      };
    }>;
  }>;
  groupId: GroupedItem["groupId"];
  excludeIds: Set<string>;
}): GroupedItem[] {
  const { workplaces, groupId, excludeIds } = params;
  const items: GroupedItem[] = [];

  if (isDefined(workplaces)) {
    for (const workplacePage of workplaces.pages) {
      for (const workplace of workplacePage.data) {
        // Filter out excluded workplaces to avoid duplicates across groups
        if (excludeIds.has(workplace.id)) {
          continue;
        }

        items.push(
          createWorkplaceGroupedItem({
            workplaceId: workplace.id,
            workplaceName: workplace.attributes.name,
            groupId,
            distanceFromWorker: workplace.attributes.distanceFromWorker,
            openShiftCount: workplace.attributes.openShiftCount,
          })
        );
      }
    }
  }

  return items;
}

export type WorkplacesSelectionMode = "search" | "discovery";

function getOtherWorkplaceTitle(params: {
  mode: WorkplacesSelectionMode;
  favoriteWorkplaceItems: GroupedItem[];
  activeWorkplaceItems: GroupedItem[];
}) {
  const { mode, favoriteWorkplaceItems, activeWorkplaceItems } = params;
  if (mode === "search") {
    return "Matching workplaces";
  }

  if (favoriteWorkplaceItems.length + activeWorkplaceItems.length === 0) {
    return "All workplaces";
  }

  return "Other workplaces";
}

export function useWorkplacesSelection() {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearchTerm = useDebounce({
    value: searchTerm,
    debounceTimeInMs: 500,
  });
  const mode: WorkplacesSelectionMode = debouncedSearchTerm ? "search" : "discovery";

  const workerGeoLocation = useWorkerGeoLocation();

  const { maxFavoriteCount } = useFavoriteWorkplacesFlag();
  const {
    favoriteWorkplaces,
    favoriteWorkplacesCount,
    isLoading: isLoadingFavoriteWorkplaces,
    isError: isFavoriteWorkplacesError,
  } = useFavoriteWorkplaces();

  const favoriteWorkplaceIds = useMemo(
    () => new Set(favoriteWorkplaces.map((favorite) => favorite.workplaceId)),
    [favoriteWorkplaces]
  );

  const {
    data: activeWorkplaces,
    isLoading: isLoadingActiveWorkplaces,
    isError: isActiveWorkplacesError,
    isFetchingNextPage: isFetchingNextPageActiveWorkplaces,
    hasNextPage: hasNextPageActiveWorkplaces,
    fetchNextPage: fetchNextPageActiveWorkplaces,
  } = useQualifiedWorkplaces({
    filter: {
      location: workerGeoLocation,
      daysSincelastShiftPosted: { gte: 30 },
    },
    page: { size: 20 },
  });
  const {
    data: otherWorkplaces,
    isLoading: isLoadingOtherWorkplaces,
    isError: isOtherWorkplacesError,
    isFetchingNextPage: isFetchingNextPageOtherWorkplaces,
    hasNextPage: hasNextPageOtherWorkplaces,
    fetchNextPage: fetchNextPageOtherWorkplaces,
  } = useOtherWorkplaces(
    {
      filter: { query: debouncedSearchTerm },
      page: { size: 20 },
    },
    // To prevent the client from fetching too many pages, they can always search workplace that are far away
    { maxPagesToLoad: 50 }
  );

  const favoriteWorkplaceItems: GroupedItem[] = favoriteWorkplaces.map((favorite) =>
    createWorkplaceGroupedItem({
      workplaceId: favorite.workplaceId,
      workplaceName: favorite.workplace?.name,
      groupId: "favorite",
      favoriteId: favorite.id,
      openShiftCount: favorite.openShiftCount,
      distanceFromWorker: favorite.distanceFromWorker,
    })
  );

  const activeWorkplaceItems: GroupedItem[] = useMemo(() => {
    // Hide active workplaces during an active search
    if (!isDefined(activeWorkplaces) || mode === "search") {
      return [];
    }

    return createDeduplicatedWorkplaceItems({
      workplaces: activeWorkplaces,
      groupId: "active",
      excludeIds: favoriteWorkplaceIds,
    });
  }, [mode, activeWorkplaces, favoriteWorkplaceIds]);

  const activeWorkplaceIds = useMemo(() => {
    if (!isDefined(activeWorkplaces) || mode === "search") {
      return new Set<string>();
    }

    const allIds = activeWorkplaces.pages.flatMap((page) =>
      page.data.map((workplace) => workplace.id)
    );
    return new Set(allIds);
  }, [activeWorkplaces, mode]);

  const otherWorkplaceItems: GroupedItem[] = useMemo(() => {
    if (!isDefined(otherWorkplaces)) {
      return [];
    }

    // Hide other workplaces until we have fetched all active workplaces if search is not active
    if (hasNextPageActiveWorkplaces && mode === "discovery") {
      return [];
    }

    return createDeduplicatedWorkplaceItems({
      workplaces: otherWorkplaces,
      groupId: "other",
      excludeIds: new Set([...favoriteWorkplaceIds, ...activeWorkplaceIds]), // Exclude favorite and active workplaces
    });
  }, [
    hasNextPageActiveWorkplaces,
    mode,
    otherWorkplaces,
    favoriteWorkplaceIds,
    activeWorkplaceIds,
  ]);

  const otherWorkplacesCount = otherWorkplaces?.pages[0]?.count ?? 0;
  const otherWorkplacesCountAdjusted = Math.max(
    otherWorkplacesCount - (activeWorkplaceItems.length + favoriteWorkplaceItems.length),
    otherWorkplaceItems.length
  );

  const isFetchingNextPage =
    isFetchingNextPageActiveWorkplaces || isFetchingNextPageOtherWorkplaces;

  async function handleLoadMore() {
    // We first make sure to fetch all active workplaces then we start fetching other workplaces
    // Ordering should match how we are rendering the grouped items
    if (hasNextPageActiveWorkplaces && mode === "discovery") {
      await fetchNextPageActiveWorkplaces();
      return;
    }

    if (hasNextPageOtherWorkplaces) {
      await fetchNextPageOtherWorkplaces();
    }
  }

  const itemGroups = {
    favorite: {
      items: favoriteWorkplaceItems,
      count: favoriteWorkplacesCount,
      title: "Your favorite workplaces",
    },
    active: {
      items: activeWorkplaceItems,
      count: undefined,
      title: "Active workplaces near you",
    },
    other: {
      items: otherWorkplaceItems,
      count: otherWorkplacesCountAdjusted,
      title: getOtherWorkplaceTitle({ mode, favoriteWorkplaceItems, activeWorkplaceItems }),
    },
  } satisfies ItemGroups;

  return {
    searchTerm,
    setSearchTerm,
    mode,
    maxFavoriteCount,
    itemGroups,
    isLoading: isLoadingFavoriteWorkplaces || isLoadingActiveWorkplaces || isLoadingOtherWorkplaces,
    isError: isFavoriteWorkplacesError || isActiveWorkplacesError || isOtherWorkplacesError,
    isFetchingNextPage,
    handleLoadMore,
  };
}
